import { forwardRef, ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

type FilterChipProps = {
  label: string;
  /** Selections behind this chip; the badge replaces the chevron once there are any. */
  count?: number;
  icon?: ReactNode;
  isOpen: boolean;
  onClick: () => void;
  onMouseDown?: () => void;
  ariaLabel: string;
};

/**
 * A filter trigger in the search page's chip row. Same pill as the search bar's
 * inline filters, standing on its own below the input.
 */
export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  function FilterChip(
    { label, count = 0, icon, isOpen, onClick, onMouseDown, ariaLabel },
    ref,
  ) {
    const hasCount = count > 0;

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        onMouseDown={onMouseDown}
        className={cn(
          // Figma header spec: 32px tall chips, 8px leading / 6px trailing padding.
          "flex items-center gap-1.5 h-8 pl-2 pr-1.5 rounded-[8px] transition-colors focus-blue whitespace-nowrap",
          "border border-border-token-brand-minimal bg-bg-brand-secondary-default",
          "hover:bg-bg-brand-secondary-hover",
          (isOpen || hasCount) && "bg-bg-brand-secondary-active",
        )}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {icon}
        <span className="label-m-medium text-fg-neutral-strong">{label}</span>
        {hasCount ? (
          <span
            className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
            aria-hidden="true"
          >
            {count}
          </span>
        ) : (
          <ChevronDown
            size={16}
            className={cn(
              "text-fg-neutral-subtle transition-transform",
              isOpen && "rotate-180",
            )}
            strokeWidth={1.5}
            aria-hidden="true"
          />
        )}
      </button>
    );
  },
);

export default FilterChip;
